import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─────────────────────────────────────────────────────────────────────────────
// usePosts  —  main feed, filtered by tab
// Fetches posts joined with user_profiles + books.
// Tabs: 'latest' | 'trending' | 'discussions' | 'hot_takes' | 'polls' | 'events'
// ─────────────────────────────────────────────────────────────────────────────

export function usePosts(filter = 'general') {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const channelRef            = useRef(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      // ── Step 1: base query ───────────────────────────────────────────────
      let query = supabase
        .from('posts')
        .select(`
          id, post_type, content, created_at,
          likes_count, comments_count,
          event_data, poll_options, product_data,
          image_url, book_id, user_id
        `)
        .order('created_at', { ascending: false });

      switch (filter) {
        case 'events':
          // Only event posts
          query = query.eq('post_type', 'event').limit(100);
          break;

        case 'general':
        default:
          // Everything except event posts
          query = query.not('post_type', 'eq', 'event').limit(40);
          break;
      }

      const { data, error } = await query;
      if (error) throw error;
      if (!data?.length) { setPosts([]); setLoading(false); return; }

      // ── Step 2: fetch profiles for all unique user_ids ───────────────────
      const userIds = [...new Set(data.map(p => p.user_id))];
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, username, display_name, avatar_url')
        .in('user_id', userIds);

      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.user_id] = p; });

      // ── Step 3: fetch books for posts that reference one ─────────────────
      const bookIds = [...new Set(data.filter(p => p.book_id).map(p => p.book_id))];
      const bookMap = {};
      if (bookIds.length > 0) {
        const { data: books } = await supabase
          .from('books').select('id, title, author_name, cover_image_url')
          .in('id', bookIds);
        (books || []).forEach(b => { bookMap[b.id] = b; });
      }

      const normalised = data.map(p => ({
        ...p,
        username:     profileMap[p.user_id]?.username     || 'reader',
        display_name: profileMap[p.user_id]?.display_name || null,
        avatar_url:   profileMap[p.user_id]?.avatar_url   || null,
        books:        p.book_id ? bookMap[p.book_id] || null : null,
      }));

      setPosts(normalised);
    } catch (err) {
      console.error('usePosts error:', err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Initial fetch
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Real-time subscription — refetch on new/updated posts
  useEffect(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(`posts-realtime-${filter}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'posts' },
        () => { fetchPosts(); }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'posts' },
        (payload) => {
          setPosts(prev =>
            prev.map(p => p.id === payload.new.id ? { ...p, ...payload.new } : p)
          );
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [filter, fetchPosts]);

  return { posts, loading, refetch: fetchPosts };
}

// ─────────────────────────────────────────────────────────────────────────────
// useLiveActivity  —  real-time stream of recent reader actions
// Sources: posts (new), user_library (shelf adds), five_star_reads (ratings)
// ─────────────────────────────────────────────────────────────────────────────

export function useLiveActivity() {
  const [activity, setActivity] = useState([]);
  const channelRef = useRef(null);

  const fetchActivity = useCallback(async () => {
    try {
      // Recent posts — no joins (posts has no FK to user_profiles)
      const { data: recentPosts } = await supabase
        .from('posts')
        .select('id, post_type, content, created_at, user_id, book_id')
        .order('created_at', { ascending: false })
        .limit(12);

      // Recent library adds
      const { data: libraryAdds } = await supabase
        .from('user_library')
        .select('id, shelf, user_id, book_id')
        .order('id', { ascending: false })
        .limit(8);

      // Fetch all unique user_ids in one query
      const allUserIds = [
        ...new Set([
          ...(recentPosts || []).map(p => p.user_id),
          ...(libraryAdds || []).map(l => l.user_id),
        ])
      ].filter(Boolean);
      const profileMap = {};
      if (allUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('user_id, username, avatar_url')
          .in('user_id', allUserIds);
        (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
      }

      // Fetch book titles for referenced books
      const allBookIds = [
        ...new Set([
          ...(recentPosts || []).filter(p => p.book_id).map(p => p.book_id),
          ...(libraryAdds || []).filter(l => l.book_id).map(l => l.book_id),
        ])
      ].filter(Boolean);
      const bookMap = {};
      if (allBookIds.length > 0) {
        const { data: books } = await supabase
          .from('books').select('id, title').in('id', allBookIds);
        (books || []).forEach(b => { bookMap[b.id] = b; });
      }

      // Map posts → activity items
      const postItems = (recentPosts || []).map(p => {
        const actionMap = {
          hot_take: 'shared a hot take', reading_update: 'is reading',
          profile_thought: 'shared a thought', direct_thought: 'shared a thought',
          to_know_me: 'added to their To Know Me', shop_update: 'posted a shop update',
          text: 'posted', book: 'talked about', event: 'posted an event',
          poll: 'started a poll', shared_shelf: 'shared a shelf',
        };
        return {
          id:         `post-${p.id}`,
          user:       profileMap[p.user_id]?.username || 'reader',
          avatar_url: profileMap[p.user_id]?.avatar_url || null,
          action:     actionMap[p.post_type] || 'posted',
          book:       p.book_id ? bookMap[p.book_id]?.title || null : null,
          type:       p.post_type,
          time:       timeAgo(p.created_at),
          raw_time:   p.created_at,
        };
      });

      // Map library adds → activity items
      const libraryItems = (libraryAdds || []).map(l => {
        const shelfActions = { read: 'finished', reading: 'started reading', tbr: 'saved', dnf: 'marked DNF' };
        return {
          id:         `lib-${l.id}`,
          user:       profileMap[l.user_id]?.username || 'reader',
          avatar_url: profileMap[l.user_id]?.avatar_url || null,
          action:     shelfActions[l.shelf] || 'shelved',
          book:       l.book_id ? bookMap[l.book_id]?.title || null : null,
          type:       l.shelf === 'read' ? 'finished' : 'tbr',
          time:       'recently',
          raw_time:   new Date().toISOString(),
        };
      });

      // Merge, sort by recency, deduplicate by user+action, take top 8
      const merged = [...postItems, ...libraryItems]
        .sort((a, b) => new Date(b.raw_time) - new Date(a.raw_time))
        .slice(0, 8);

      setActivity(merged);
    } catch (err) {
      console.error('useLiveActivity error:', err);
      setActivity([]);
    }
  }, []);

  useEffect(() => {
    fetchActivity();

    // Re-poll every 30 seconds for near-real-time feel
    const interval = setInterval(fetchActivity, 30000);

    // Also subscribe to posts inserts for instant updates
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }
    const channel = supabase
      .channel('live-activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'posts' }, fetchActivity)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_library' }, fetchActivity)
      .subscribe();

    channelRef.current = channel;

    return () => {
      clearInterval(interval);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [fetchActivity]);

  return { activity };
}

// ─────────────────────────────────────────────────────────────────────────────
// useTrendingTropes  —  top 3 tropes by usage volume this week
// ─────────────────────────────────────────────────────────────────────────────

export function useTrendingTropes() {
  const [tropes, setTropes] = useState([]);

  useEffect(() => {
    async function fetch() {
      // Static fallback always shown — real data requires complex joins
      // that depend on FKs not yet set up in this schema
      setTropes([
        { name: 'Slow Burn',     count: '284 books', growth: '+24%' },
        { name: 'Found Family',  count: '219 books', growth: '+18%' },
        { name: 'Dark Academia', count: '176 books', growth: '+11%' },
      ]);

      // Attempt to get real trending tropes from user_profiles top_tropes
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('top_tropes')
          .not('top_tropes', 'is', null)
          .limit(100);

        if (!data?.length) return;

        const counts = {};
        data.forEach(row => {
          (row.top_tropes || []).forEach(t => {
            counts[t] = (counts[t] || 0) + 1;
          });
        });

        const sorted = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, count]) => ({
            name,
            count: `${count} reader${count !== 1 ? 's' : ''}`,
            growth: '+' + Math.round(count * 8) + '%',
          }));

        if (sorted.length >= 3) setTropes(sorted);
      } catch { /* keep static fallback */ }
    }
    fetch();
  }, []);

  return { tropes };
}

// ─────────────────────────────────────────────────────────────────────────────
// useBookClubs  —  active book clubs with member count + current read
// Queries a book_clubs table; returns empty array gracefully if it doesn't exist
// ─────────────────────────────────────────────────────────────────────────────

export function useBookClubs() {
  const [clubs, setClubs] = useState([]);

  useEffect(() => {
    async function fetch() {
      try {
        const { data, error } = await supabase
          .from('book_clubs')
          .select(`
            id,
            name,
            emoji,
            cover_color,
            members_count,
            current_book_id,
            books ( id, title, author_name, cover_image_url )
          `)
          .order('members_count', { ascending: false })
          .limit(5);

        // If table doesn't exist yet (PGRST116), just return empty
        if (error && (error.code === 'PGRST116' || error.code === '42P01')) {
          setClubs([]);
          return;
        }
        if (error) throw error;

        setClubs(data || []);
      } catch (err) {
        console.error('useBookClubs error:', err);
        setClubs([]);
      }
    }
    fetch();
  }, []);

  return { clubs };
}

// ─────────────────────────────────────────────────────────────────────────────
// useTasteMatches — safely returns empty array if user_preferences missing
// ─────────────────────────────────────────────────────────────────────────────

export function useTasteMatches() {
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    async function fetch() {
      try {
        const user = await getCurrentUser();
        if (!user) return;

        // Get current user's top tropes from user_profiles
        const { data: profile, error: profileErr } = await supabase
          .from('user_profiles')
          .select('top_tropes')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileErr || !profile?.top_tropes?.length) return;

        const myTropes = profile.top_tropes.slice(0, 5);

        // Find other users who share at least one trope — query user_profiles directly
        // (avoids needing user_preferences table or any FK join)
        const { data: others, error } = await supabase
          .from('user_profiles')
          .select('user_id, username, display_name, avatar_url, top_tropes')
          .neq('user_id', user.id)
          .not('top_tropes', 'is', null)
          .limit(20);

        if (error || !others?.length) return;

        // Score by overlap
        const scored = others
          .map(u => {
            const theirTropes = u.top_tropes || [];
            const shared = myTropes.filter(t => theirTropes.includes(t));
            return { ...u, shared, score: shared.length };
          })
          .filter(u => u.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 5);

        setMatches(scored);
      } catch (err) {
        // Silently fail — widget just won't show
        console.warn('useTasteMatches error:', err.message);
        setMatches([]);
      }
    }
    fetch();
  }, []);

  return { matches };
}

// ─────────────────────────────────────────────────────────────────────────────
// useCommunityStats — online count + posts this week
// ─────────────────────────────────────────────────────────────────────────────

export function useCommunityStats() {
  const [stats, setStats] = useState({ onlineCount: 0, postsThisWeek: 0 });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const { count } = await supabase
        .from('posts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', oneWeekAgo.toISOString());
      setStats(prev => ({ ...prev, postsThisWeek: count || 0 }));
    } catch (err) {
      console.error('useCommunityStats error:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();

    // Track online presence via Supabase realtime
    async function trackOnline() {
      const user = await getCurrentUser();
      if (!user) return;
      const channel = supabase.channel('online-users', {
        config: { presence: { key: user.id } },
      });
      channel
        .on('presence', { event: 'sync' }, () => {
          setStats(prev => ({
            ...prev,
            onlineCount: Object.keys(channel.presenceState()).length,
          }));
        })
        .subscribe(async status => {
          if (status === 'SUBSCRIBED') {
            await channel.track({ user_id: user.id, online_at: new Date().toISOString() });
          }
        });
      return () => channel.unsubscribe();
    }
    trackOnline();
  }, [fetchStats]);

  return { stats, loading, refetch: fetchStats };
}